import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { GridColDef } from '@mui/x-data-grid';
import { CrudListPage } from '@core/components';
import { useCrudListPage } from '@core/hooks/useCrudListPage';
import {
  type Customer,
  type CustomerQuery,
  customerKeys,
  deleteCustomer,
  getCustomersPage,
} from '../../api/customer';
import CustomerDialog from '../components/CustomerDialog';
import CustomerFilterDrawer from '../components/CustomerFilterDrawer';
import { useAppContext } from '../context/AppContext';
import { navItems } from '../navigation';
import {
  type CustomerFilters,
  EMPTY_CUSTOMER_FILTERS,
  countActiveCustomerFilters,
  customerFiltersToQueryParams,
  getFullName,
} from '../utils/customer';

const Customers = () => {
  const queryClient = useQueryClient();
  const { refreshContext } = useAppContext();
  const {
    filteredItems: filteredCustomers,
    loading,
    setSearchTerm,
    filters,
    setFilters,
    clearFilters,
    filterOpen,
    openFilters,
    closeFilters,
    activeFilterCount,
    openCreate,
    dialogOpen,
    editingItem: editingCustomer,
    deleteConfirm,
    load: loadCustomers,
    closeDialog,
    cancelDelete,
    confirmDelete,
    getRowId,
    contextMenuItems,
    paginationMode,
    rowCount,
    paginationModel,
    onPaginationModelChange,
    pageSizeOptions,
  } = useCrudListPage<Customer, CustomerFilters, CustomerQuery>({
    loadPage: getCustomersPage,
    toQueryParams: customerFiltersToQueryParams,
    deleteItem: async (customer) => {
      await deleteCustomer(customer.id);
      await queryClient.invalidateQueries({ queryKey: customerKeys.all });
    },
    loadErrorMessage: 'Failed to load customers',
    deleteSuccessMessage: 'Customer deleted',
    deleteErrorMessage: 'Failed to delete customer',
    refreshContext,
    filter: {
      empty: EMPTY_CUSTOMER_FILTERS,
      countActive: countActiveCustomerFilters,
    },
  });

  const columns = useMemo<GridColDef<Customer>[]>(
    () => [
      {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 180,
        sortable: false,
        valueGetter: (_, row) => getFullName(row),
      },
      {
        field: 'email',
        headerName: 'Email',
        flex: 1,
        minWidth: 180,
        sortable: false,
      },
      {
        field: 'phone',
        headerName: 'Phone',
        flex: 1,
        minWidth: 180,
        sortable: false,
      },
    ],
    []
  );

  return (
    <>
      <CrudListPage<Customer>
        navItems={navItems}
        path="/customers"
        rows={filteredCustomers}
        columns={columns}
        loading={loading}
        getRowId={getRowId}
        contextMenuItems={contextMenuItems}
        paginationMode={paginationMode}
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={pageSizeOptions}
        searchConfig={{
          placeholder: 'Search customers...',
          onSearch: setSearchTerm,
        }}
        filterConfig={{
          activeCount: activeFilterCount,
          onOpen: openFilters,
        }}
        addConfig={{ label: 'Add Customer', onAdd: openCreate }}
        addEditDialog={
          <CustomerDialog
            open={dialogOpen}
            onClose={closeDialog}
            customer={editingCustomer}
            onSuccess={() => loadCustomers()}
          />
        }
        deleteConfig={{
          title: 'Delete customer',
          item: deleteConfirm,
          getContent: (customer) =>
            `Are you sure you want to delete "${getFullName(customer)}"?`,
          onConfirm: confirmDelete,
          onCancel: cancelDelete,
        }}
      />
      <CustomerFilterDrawer
        open={filterOpen}
        filters={filters}
        onApply={setFilters}
        onClear={clearFilters}
        onClose={closeFilters}
      />
    </>
  );
};

export default Customers;
